import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  IRetryPolicy,
  LogLevel,
  RetryContext,
} from "@microsoft/signalr";
import { API_BASE_URL } from "../../../config";

/** Which token opens the connection: the instructor's or the member portal's. */
export type LiveKind = "staff" | "portal";

/**
 * `connecting` before the first connection, `connected` while signals flow, `reconnecting` once a
 * live connection was lost, `unavailable` when it never came up (e.g. the path is blocked). Only
 * `connected` pauses the 30 s poll; only `reconnecting` shows the notice.
 */
export type LiveStatus = "connecting" | "connected" | "reconnecting" | "unavailable";

/** What a screen does on a signal. No content arrives; the screen fetches over HTTP. */
export interface LiveHandlers {
  onThreadChanged?: (threadId: string) => void;
  onUnreadChanged?: () => void;
  /** Someone's online bit flipped (US-91): refetch the thread header. */
  onPresenceChanged?: () => void;
  /** On every (re)connect: refetch whatever might have been missed. */
  onResync?: () => void;
}

/** The subset of a SignalR connection the client uses (a fake in tests). */
export type LiveConnection = Pick<
  HubConnection,
  "start" | "stop" | "on" | "onclose" | "onreconnecting" | "onreconnected" | "invoke"
>;

export type LiveConnectionFactory = (accessToken: () => string) => LiveConnection;

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/** How long the connection outlives its last screen, so navigating between screens keeps it. */
export const STOP_GRACE_MS = 2_000;

/** Delays between reconnect attempts; the last one repeats for as long as the screen is open. */
export const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/** How often a live connection tells the hub it is still here; three missed beats read offline (PD-0040). */
export const HEARTBEAT_MS = 30_000;

export const reconnectDelay = (attempt: number): number =>
  RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];

const retryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds: (context: RetryContext): number => reconnectDelay(context.previousRetryCount),
};

/** Websockets only, straight to the hub (no negotiate round trip), token on the handshake. */
export const createSignalRConnection: LiveConnectionFactory = (accessToken) => {
  const connection = new HubConnectionBuilder()
    .withUrl(`${API_BASE_URL}/api/hubs/messages`, {
      transport: HttpTransportType.WebSockets,
      skipNegotiation: true,
      accessTokenFactory: accessToken,
    })
    .withAutomaticReconnect(retryPolicy)
    .configureLogging(LogLevel.None)
    .build();
  connection.keepAliveIntervalInMilliseconds = 15_000;
  connection.serverTimeoutInMilliseconds = 30_000;
  return connection;
};

/**
 * One live connection per token kind for the whole app session. Screens `retain` it while mounted;
 * the last `release` (sign-out unmounts them all) stops it. Watched threads are re-watched after
 * every reconnect.
 */
export class MessagesHubClient {
  private connection: LiveConnection | null = null;
  private users = 0;
  private attempt = 0;
  private everConnected = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private openedWith: string | null | undefined = null;
  private status: LiveStatus = "connecting";
  private readonly handlers = new Set<LiveHandlers>();
  private readonly statusListeners = new Set<(status: LiveStatus) => void>();
  private readonly watched = new Map<string, number>();

  constructor(
    private readonly accessToken: () => string | null | undefined,
    private readonly factory: LiveConnectionFactory = createSignalRConnection,
  ) {}

  getStatus(): LiveStatus {
    return this.status;
  }

  retain(): () => void {
    this.users += 1;
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
      // Signed out and in as someone else within the grace: never keep the old identity's socket.
      if (this.accessToken() !== this.openedWith) {
        this.shutdown();
        void this.start();
      }
    } else if (this.users === 1) void this.start();
    return () => {
      this.users -= 1;
      // A route change unmounts one screen before the next mounts; keep the socket across it.
      if (this.users === 0) this.stopTimer = setTimeout(() => this.shutdown(), STOP_GRACE_MS);
    };
  }

  subscribe(handlers: LiveHandlers): () => void {
    this.handlers.add(handlers);
    return () => this.handlers.delete(handlers);
  }

  onStatus(listener: (status: LiveStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  watch(threadId: string): () => void {
    const count = this.watched.get(threadId) ?? 0;
    this.watched.set(threadId, count + 1);
    if (count === 0 && this.status === "connected") this.invoke("Watch", threadId);
    return () => {
      const left = (this.watched.get(threadId) ?? 1) - 1;
      if (left > 0) {
        this.watched.set(threadId, left);
        return;
      }
      this.watched.delete(threadId);
      if (this.status === "connected") this.invoke("Unwatch", threadId);
    };
  }

  private async start(): Promise<void> {
    this.retryTimer = null;
    if (this.users === 0) return;
    const token = this.accessToken();
    if (!token) {
      this.scheduleRestart();
      return;
    }
    this.openedWith = token;
    const connection = this.factory(() => this.accessToken() ?? "");
    this.connection = connection;
    connection.on("threadChanged", (threadId: string) => this.handlers.forEach((h) => h.onThreadChanged?.(threadId)));
    connection.on("unreadChanged", () => this.handlers.forEach((h) => h.onUnreadChanged?.()));
    connection.on("presenceChanged", () => this.handlers.forEach((h) => h.onPresenceChanged?.()));
    connection.onreconnecting(() => this.setStatus("reconnecting"));
    connection.onreconnected(() => this.connected());
    connection.onclose(() => {
      if (this.connection === connection) this.scheduleRestart();
    });
    try {
      await connection.start();
      if (this.connection === connection) this.connected();
    } catch (error: unknown) {
      // Expected while the API is down or the path is blocked; the poll covers it meanwhile.
      if (this.attempt === 0) console.warn("Messages connection unavailable, polling instead:", errorMessage(error));
      if (this.connection === connection) this.scheduleRestart();
    }
  }

  private connected(): void {
    this.attempt = 0;
    this.everConnected = true;
    this.setStatus("connected");
    this.startHeartbeat();
    this.watched.forEach((_, threadId) => this.invoke("Watch", threadId));
    this.handlers.forEach((h) => h.onResync?.());
  }

  private scheduleRestart(): void {
    this.stopHeartbeat();
    this.connection = null;
    if (this.users === 0) return;
    this.setStatus(this.everConnected ? "reconnecting" : "unavailable");
    const delay = reconnectDelay(this.attempt);
    this.attempt += 1;
    this.retryTimer = setTimeout(() => void this.start(), delay);
  }

  private shutdown(): void {
    this.stopTimer = null;
    this.stopHeartbeat();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const connection = this.connection;
    this.connection = null;
    this.attempt = 0;
    this.everConnected = false;
    this.setStatus("connecting");
    void connection?.stop().catch((error: unknown) => console.warn("Failed to stop the messages connection:", errorMessage(error)));
  }

  private startHeartbeat(): void {
    if (this.heartbeat) return;
    this.heartbeat = setInterval(() => {
      if (this.status !== "connected") return;
      void this.connection
        ?.invoke("Heartbeat")
        .catch((error: unknown) => console.warn("Messages heartbeat failed:", errorMessage(error)));
    }, HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /** A refused watch (not a participant) only means no signals for that thread; its HTTP fetch answers 404 anyway. */
  private invoke(method: "Watch" | "Unwatch", threadId: string): void {
    void this.connection
      ?.invoke(method, threadId)
      .catch((error: unknown) => console.warn(`Messages connection refused ${method}:`, errorMessage(error)));
  }

  private setStatus(status: LiveStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => listener(status));
  }
}
